import React, { useState, useRef } from "react";
import { FaCamera } from "react-icons/fa";
import { FiUser } from "react-icons/fi";
import { Capacitor } from "@capacitor/core";
import { OnboardingData } from "../../../hooks/useOnboardingState";
import ImageCropperModal from "../../../components/ImageCropperModal";
import NotificationsOffPopup from "../../../components/NotificationsOffPopup";
import { pickAvatarPhotoNative } from "../../../lib/avatarPhoto";
import { InvitedByChip, useReferralInvite } from "../../../components/ReferralInvite";
import { sanitizeInstagramHandle } from "../../../utils/instagramHandle";
import { t } from "../../../i18n";

interface Props {
  data: OnboardingData;
  updateData: (updates: Partial<OnboardingData>) => void;
  onNext: () => void;
  onBack: () => void;
}

// The one profile screen of sign-up (founder, 1.5.1 #1: photo, name and
// Instagram together; venue and bio left sign-up for Edit profile). With the
// Connect Instagram step on (build 54, data.igStep): photo and name only —
// Instagram is proven on the next screen, never typed.
export default function Step2Identity({ data, updateData, onNext }: Props) {
  const [name, setName] = useState(data.name);
  const [instagram, setInstagram] = useState(data.instagram);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(data.avatarPreview);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [error, setError] = useState("");
  const invite = useReferralInvite(data.referralCode);
  // Photo-library/camera permission was denied at the OS level — show the
  // Settings deep-link popup instead of the old silent no-op.
  const [showPhotoAccessPopup, setShowPhotoAccessPopup] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarClick = async () => {
    const isNative = Capacitor.isNativePlatform();

    if (!isNative) {
      fileInputRef.current?.click();
      return;
    }

    const result = await pickAvatarPhotoNative();
    if (result.status === "ok") {
      // Hand the raw photo to the cropper instead of saving it directly —
      // gives the talent the same pinch/zoom/circle framing we use for the
      // QR-center logo, so avatars come out square (no squashed faces).
      setCropSrc(result.dataUrl);
    } else if (result.status === "denied") {
      setShowPhotoAccessPopup(true);
    } else if (result.status === "error") {
      window.alert(t("step2Identity.couldnTOpenYour"));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      if (ev.target?.result) setCropSrc(ev.target.result as string);
    };
    reader.readAsDataURL(file);
    // Reset so picking the same file again re-fires onChange.
    e.target.value = "";
  };

  const handleCropConfirm = async (croppedDataUrl: string) => {
    try {
      const resp = await fetch(croppedDataUrl);
      const blob = await resp.blob();
      const file = new File([blob], "avatar.png", { type: "image/png" });
      setAvatarPreview(croppedDataUrl);
      updateData({ avatarFile: file, avatarPreview: croppedDataUrl });
    } catch (err) {
      console.error("Failed to finalize cropped avatar:", err);
    } finally {
      setCropSrc(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const cleaned = name.trim();
    if (!cleaned) {
      setError(t("step2Identity.pleaseEnterYourName"));
      return;
    }

    // The public URL is generated server-side from the name (User model
    // pre-validate hook), so we just need a name with at least two
    // letters/numbers for it to build a real link from.
    if (cleaned.replace(/[^a-z0-9]/gi, "").length < 2) {
      setError(t("step2Identity.yourArtistNameNeeds"));
      return;
    }

    // Instagram is optional (App Review 5.1.1(v)): a blank one continues.
    // With its own step on, nothing typed is kept.
    updateData({ name: cleaned, instagram: data.igStep ? "" : sanitizeInstagramHandle(instagram), referralCode: invite.code });
    onNext();
  };

  return (
    <div className="onboarding-step step2-identity">
      {/* A /join link's "Invited by …" chip; there is no typed referral code
          at sign-up any more (founder, 10/30), so × just drops the invite. */}
      {invite.referrer && <InvitedByChip referrer={invite.referrer} onRemove={() => invite.setCode("")} />}
      {/* No subtitle and a 104px photo (the gallery 1A screen): with the
          Instagram field here too, that keeps the step to one screen on every
          notched iPhone, so the button never moves. */}
      <h2 className="step-title">{t("step2Identity.setUpYourProfile")}</h2>

      <form onSubmit={handleSubmit} className="step2-form">
        {/* Avatar upload */}
        <div className="avatar-upload-section">
          <div className="avatar-upload-wrapper" onClick={handleAvatarClick} style={{ width: 104, height: 104 }}>
            {avatarPreview ? (
              <img src={avatarPreview} alt={t("step2Identity.avatar")} className="avatar-preview" />
            ) : (
              <div className="avatar-placeholder" style={{ width: 104, height: 104, fontSize: '0.75rem' }}>
                <FaCamera size={22} />
                <span>{t("step2Identity.addPhoto")}</span>
              </div>
            )}
            <div className="avatar-edit-badge">
              <FaCamera size={12} />
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </div>

        <div className="input-group">
          <label htmlFor="name">
            <span>{t("step2Identity.displayName")}</span>
          </label>
          <div className="name-input-wrapper">
            <FiUser className="name-input-icon" aria-hidden />
            <input
              id="name"
              type="text"
              placeholder={t("step2Identity.yourNameOrStage")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={30}
              className={name ? undefined : "attention"}
            />
          </div>
        </div>

        {/* Moved here from the old second profile screen; its label and
            placeholder keep the step4Enhancements.* keys. Just "Instagram",
            hint "Verify your Instagram." (founder, 10/30); still not required.
            Gone while sign-up has its Connect Instagram step (build 54). */}
        {!data.igStep && <div className="input-group">
          <label htmlFor="instagram">
            <span className="label-with-icon">
              {t("step4Enhancements.instagram")}
              <svg className="instagram-icon" width="16" height="16" viewBox="0 0 448 512" aria-hidden="true">
                <defs>
                  <linearGradient id="instagram-gradient-step4" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FFDC80" />
                    <stop offset="25%" stopColor="#F77737" />
                    <stop offset="50%" stopColor="#E1306C" />
                    <stop offset="75%" stopColor="#C13584" />
                    <stop offset="100%" stopColor="#833AB4" />
                  </linearGradient>
                </defs>
                <path fill="url(#instagram-gradient-step4)" d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
              </svg>
            </span>
          </label>
          <div className="instagram-input-wrapper">
            <span className="instagram-prefix">@</span>
            <input
              id="instagram"
              type="text"
              placeholder={t("step4Enhancements.username")}
              value={instagram}
              onChange={(e) => setInstagram(sanitizeInstagramHandle(e.target.value))}
              maxLength={30}
            />
          </div>
          <p className="input-hint">{t("step2Identity.verifyYourInstagram")}</p>
        </div>}

        {error && <p className="step-error">{error}</p>}

        <div className="ob-footer">
          <button type="submit" className="ob-cta ob-cta--green">
            {t("step2Identity.continue")}
            <svg className="ob-cta-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
          </button>
        </div>
      </form>

      {cropSrc && (
        <ImageCropperModal
          imageSrc={cropSrc}
          onCancel={() => setCropSrc(null)}
          onConfirm={handleCropConfirm}
          cropShape="round"
        />
      )}

      <NotificationsOffPopup
        open={showPhotoAccessPopup}
        onClose={() => setShowPhotoAccessPopup(false)}
        emoji="📷"
        title={t("step2Identity.photoAccessIsOff")}
        sub={t("step2Identity.allowPhotoAccessTo")}
      />
    </div>
  );
}
